import { Offer } from "../Entities/Offer";
import { billingPeriod, Plan } from "../Entities/Plan";
import { EventDispatcher } from "../Utils/event-dispatcher";
import offerService from "./offer.service";
import planService from "./plan.service";

type BasePlan = {
  planId: string | null;
  billedAnnually: boolean;
};

export type Addons = {
  addonId: string;
  quantity: number;
};

export type Cart = {
  basePlan: BasePlan;
  addons: Addons[];
  appliedOffer: string | null;
  appliedCoupon: string | null;
};

export type CartSummary = {
  selectedPlan: Plan | null | undefined;
  selectedPlanPrice: number;
  billingPeriod: billingPeriod;
  applicationAddonCount: number;
  applicationAddonPrice: number;
  jobPostsAddonCount: number;
  jobPostsAddonPrice: number;
  totalPrice: number;
  tax: number;
  discount: number;
  totalPriceAfterDiscount: number;
  discountAmount: number;
  appliedOffer: Offer | null | undefined;
  currency: "INR" | "USD";
};

class CartManager implements Cart {
  basePlan: BasePlan;
  addons: Addons[];
  appliedOffer: string | null;
  appliedCoupon: string | null;

  constructor() {
    this.basePlan = { planId: null, billedAnnually: true };
    this.addons = [];
    this.appliedOffer = null;
    this.appliedCoupon = null;
  }
}

class CartService extends EventDispatcher {
  cart: Cart;

  constructor() {
    super();
    this.cart = new CartManager();
  }

  private updateCart(newCart: Cart) {
    this.cart = newCart;
    this.dispatch("CART_CHANGED", this.cart);
  }

  subscribe(event: string, callback: (...args: any[]) => void) {
    super.subscribe(event, callback);
  }

  unsubscribe(event: string, callback: (...args: any[]) => void) {
    super.unsubscribe(event, callback);
  }

  getCartData() {
    return this.cart;
  }

  setSelectedBasePlan(planId: string) {
    this.updateCart({
      ...this.cart,
      basePlan: { ...this.cart.basePlan, planId },
    });
  }

  setBillingPeriod(billedAnnually: boolean) {
    this.updateCart({
      ...this.cart,
      basePlan: { ...this.cart.basePlan, billedAnnually },
      addons: [],
      appliedOffer: null,
      appliedCoupon: null,
    });
  }

  applyOffer(offerId: string) {
    this.updateCart({
      ...this.cart,
      appliedOffer: offerId,
    });
  }

  removeOffer() {
    this.updateCart({
      ...this.cart,
      appliedOffer: null,
      appliedCoupon: null,
    });
  }

  setSelectedAddons(addonId: string) {
    const addonExists = this.cart.addons.find((a) => a.addonId === addonId);

    const updatedAddons = addonExists
      ? this.cart.addons.map((a) =>
          a.addonId === addonId ? { ...a, quantity: a.quantity + 1 } : a
        )
      : [...this.cart.addons, { addonId, quantity: 1 }];

    this.updateCart({
      ...this.cart,
      addons: updatedAddons,
    });

    return updatedAddons;
  }

  clearAddons(addonId: string) {
    const updatedAddons = this.cart.addons.filter((a) => a.addonId !== addonId);

    this.updateCart({
      ...this.cart,
      addons: updatedAddons,
    });
  }

  clearCart() {
    this.updateCart(new CartManager());
  }

  setCart(cart: Cart) {
    this.updateCart({ ...cart });
  }

  async getCartSummary(
    purchaseType: "addon-only" | "normal",
    addonId: string | null,
    userCurrencyCode: "INR" | "USD"
  ): Promise<CartSummary | null> {
    if (purchaseType === "normal" && !this.cart.basePlan.planId) return null;

    let selectedPlan: Plan | null | undefined = null;
    let selectedPlanPrice = 0;
    let billingPeriod: billingPeriod = "month";
    let quantityOfAddon = 0;

    if (purchaseType === "normal" && this.cart.basePlan.planId) {
      billingPeriod = this.cart.basePlan.billedAnnually ? "year" : "month";
      selectedPlan = await planService.getPlanById(
        this.cart.basePlan.planId
      );

      const selectedPlanVariant = selectedPlan?.variants.find(
        (v) =>
          v.currency.toLowerCase() === userCurrencyCode.toLowerCase() &&
          v.billingPeriod === billingPeriod
      );

      selectedPlanPrice = selectedPlanVariant?.price || 0;
    } else if (purchaseType === "addon-only" && addonId) {
      selectedPlan = await planService.getPlanById(addonId);
      const selectedPlanVariant = selectedPlan?.variants.find(
        (v) =>
          v.currency.toLowerCase() === userCurrencyCode.toLowerCase() &&
          v.billingPeriod === "month"
      );
      selectedPlanPrice = selectedPlanVariant?.price || 0;
      quantityOfAddon =
        this.cart.addons.find((a) => a.addonId === addonId)?.quantity || 0;
    }

    // ADDONS
    const applicationAddonPlan =
      await planService.getAddonPlanByType("job-application");
    const jobPostsAddonPlan = await planService.getAddonPlanByType("job-post");

    const applicationAddonCount =
      this.cart.addons.find((a) => a.addonId === applicationAddonPlan?._id)
        ?.quantity || 0;
    const applicationAddonVariant = applicationAddonPlan?.variants.find(
      (v) => v.currency.toLowerCase() === userCurrencyCode.toLowerCase()
    );
    const applicationAddonPrice =
      (applicationAddonVariant?.price || 0) * applicationAddonCount;

    const jobPostsAddonCount =
      this.cart.addons.find((a) => a.addonId === jobPostsAddonPlan?._id)
        ?.quantity || 0;
    const jobPostsAddonVariant = jobPostsAddonPlan?.variants.find(
      (v) => v.currency.toLowerCase() === userCurrencyCode.toLowerCase()
    );
    const jobPostsAddonPrice =
      (jobPostsAddonVariant?.price || 0) * jobPostsAddonCount;

    // TOTAL (inclusive of tax — this is what's configured in DB/payment gateway)
    const GST_RATE = 0.18;
    const isINR = userCurrencyCode === "INR";

    const inclusivePlanPrice =
      purchaseType === "addon-only"
        ? selectedPlanPrice * quantityOfAddon
        : selectedPlanPrice;
    const inclusiveTotal =
      purchaseType === "addon-only"
        ? inclusivePlanPrice
        : inclusivePlanPrice + applicationAddonPrice + jobPostsAddonPrice;

    // Extract base prices (ex-tax) for display; GST only applies for INR
    const extractBasePrice = (inclusive: number): number =>
      isINR ? Math.round(inclusive / (1 + GST_RATE)) : inclusive;

    const exTaxPlanPrice = extractBasePrice(inclusivePlanPrice);
    const exTaxApplicationAddonPrice = extractBasePrice(applicationAddonPrice);
    const exTaxJobPostsAddonPrice = extractBasePrice(jobPostsAddonPrice);
    const tax = isINR ? inclusiveTotal - extractBasePrice(inclusiveTotal) : 0;

    // DISCOUNT (applied on inclusive total)
    let discount = 0;
    let discountAmount = 0;
    let offer: Offer | null | undefined = null;

    if (this.cart.appliedOffer) {
      offer = await offerService.getOfferById(this.cart.appliedOffer);
      if (offer) {
        discount = offer.discountValue;
        discountAmount =
          offer.discountType === "amount"
            ? offer.discountValue
            : Math.round((inclusiveTotal * offer.discountValue) / 100);

        this.updateCart({
          ...this.cart,
          appliedCoupon: offer.code,
        });
      }
    }

    // TOTAL AFTER DISCOUNT (inclusive amount the customer pays)
    const totalPriceAfterDiscount = inclusiveTotal - discountAmount;

    return {
      selectedPlan,
      selectedPlanPrice: exTaxPlanPrice,
      billingPeriod,
      applicationAddonCount,
      applicationAddonPrice: exTaxApplicationAddonPrice,
      jobPostsAddonCount,
      jobPostsAddonPrice: exTaxJobPostsAddonPrice,
      totalPrice: inclusiveTotal,
      tax,
      discount,
      discountAmount,
      totalPriceAfterDiscount,
      appliedOffer: offer,
      currency: userCurrencyCode,
    };
  }
}

const cartService = new CartService();
export default cartService;
